import { Component } from '@theme/component';

/**
 * Features section — swaps its content for the product an emitter names.
 *
 * The section's own markup carries `bind:` attributes and one `each` template, so a swap is
 * data bound into markup that is already there rather than markup built here. The data is
 * that product's config as JSON, from `templates/product.features-json.liquid`.
 *
 * A Section Rendering request would be the obvious way to do this and does not work: it
 * carries this section's own product setting, and the setting wins over the url's product,
 * so every swap would render the same thing.
 *
 * The contract is named for this component rather than for whoever asks, so any section
 * can drive it.
 *
 * @example
 * element.dispatchEvent(
 *   new CustomEvent('features:update', { bubbles: true, detail: { productUrl } }),
 * );
 */
class FeaturesSection extends Component {
  /** @type {AbortController | null} */
  #pending = null;

  /** @type {string | null} The product on screen, so the same one is not fetched twice. */
  #shown = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('features:update', this.#update);

    // Whoever drives this section may have announced its selection before this element
    // upgraded — a url or a preselected style is applied on their connect. So ask.
    document.dispatchEvent(new CustomEvent('features:request'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('features:update', this.#update);
    this.#pending?.abort();
  }

  /** @param {Event} event */
  #update = async (event) => {
    const { productUrl, target } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (!productUrl) return;

    // An emitter that named no section is addressing all of them.
    if (target && target !== this.dataset.eventId) return;

    // Already showing it: an emitter is free to say so as often as it likes.
    if (productUrl === this.#shown) return;

    const section = this.#section;
    if (!section) return;

    // Clicking through styles outruns the network, and a stale answer would win.
    this.#pending?.abort();
    const controller = new AbortController();
    this.#pending = controller;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.search = '';
      // The alternate view that renders a product's PDP Feature config as JSON.
      url.searchParams.set('view', 'features-json');

      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;

      const data = await response.json();
      // A product with no config of its own would empty the section, which is worse than
      // leaving the last one up.
      if (!data?.banners?.length) return;

      const slideshow = section.querySelector('slideshow-component');

      this.render(data, section);
      this.#shown = productUrl;

      // re-mount
      slideshow?.updatedCallback();
      slideshow?.scrollToStart();
    } catch {
      // An aborted or failed request leaves the section showing what it had.
    }
  };

  /** The rendered section this component belongs to. */
  get #section() {
    const id = this.dataset.sectionId;

    return id ? document.getElementById(`shopify-section-${id}`) : null;
  }
}

if (!customElements.get('features-section')) {
  customElements.define('features-section', FeaturesSection);
}
